import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BentoCard from '../../ui/BentoCard'
import { KILO } from '../../../constants/KILO_THEME'
import { SET_COUNTDOWN_SECONDS } from '../../../lib/calibration/tempoConstants'

type CardioPhase = 'prep' | 'countdown' | 'active'

type CalibrationCardioTrackerProps = {
  title: string
  subtitle: string
  onComplete: () => void
  busy: boolean
  error: string | null
}

const CalibrationCardioTracker = ({
  title,
  subtitle,
  onComplete,
  busy,
  error,
}: CalibrationCardioTrackerProps) => {
  const { t } = useTranslation()
  const [phase, setPhase] = useState<CardioPhase>('prep')
  const [countdownTick, setCountdownTick] = useState<number | null>(null)
  const [elapsedSec, setElapsedSec] = useState(0)
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const elapsedRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const goFlashRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimers = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current)
      countdownRef.current = null
    }
    if (elapsedRef.current) {
      clearInterval(elapsedRef.current)
      elapsedRef.current = null
    }
    if (goFlashRef.current) {
      clearTimeout(goFlashRef.current)
      goFlashRef.current = null
    }
  }, [])

  useEffect(() => () => clearTimers(), [clearTimers])

  const beginCountdown = useCallback(() => {
    clearTimers()
    setPhase('countdown')
    setCountdownTick(SET_COUNTDOWN_SECONDS)

    let tick = SET_COUNTDOWN_SECONDS
    countdownRef.current = setInterval(() => {
      tick -= 1
      if (tick > 0) {
        setCountdownTick(tick)
        return
      }

      clearTimers()
      setCountdownTick(0)
      setPhase('active')
      setElapsedSec(0)

      elapsedRef.current = setInterval(() => {
        setElapsedSec((s) => s + 1)
      }, 1000)

      goFlashRef.current = setTimeout(() => setCountdownTick(null), 500)
    }, 1000)
  }, [clearTimers])

  const formatElapsed = (sec: number) => {
    const m = Math.floor(sec / 60)
    const s = sec % 60
    return `${m}:${String(s).padStart(2, '0')}`
  }

  if (phase === 'countdown') {
    return (
      <div className={`fixed inset-0 z-[70] flex min-h-[70vh] flex-col items-center justify-center ${KILO.bg.page} px-6`}>
        <p className={KILO.meta}>{t('workout.calibration.cardio.countdownLabel')}</p>
        <p
          className="mt-4 font-black tabular-nums leading-none text-kilo-accent"
          style={{ fontSize: 'clamp(6rem, 28vw, 10rem)' }}
        >
          {countdownTick === 0 ? t('workout.calibration.tracker.countdownGo') : countdownTick}
        </p>
      </div>
    )
  }

  if (phase === 'active') {
    return (
      <div className={`fixed inset-0 z-[70] flex min-h-[70vh] flex-col gap-6 ${KILO.bg.page} px-4 pt-6`}>
        <header>
          <p className={KILO.meta}>Bio-Calibration</p>
          <h1 className={`mt-1 text-2xl ${KILO.heading}`}>{title}</h1>
        </header>

        <div className="flex flex-1 flex-col items-center justify-center">
          <p className="font-mono text-xs font-bold uppercase tracking-[0.35em] text-kilo-muted">
            {t('workout.calibration.cardio.elapsed')}
          </p>
          <p
            className="mt-3 font-black tabular-nums text-kilo-text"
            style={{ fontSize: 'clamp(4rem, 20vw, 7rem)' }}
          >
            {formatElapsed(elapsedSec)}
          </p>
          <p className="mt-6 max-w-xs text-center text-sm text-kilo-muted">
            {t('workout.calibration.cardio.activeHint')}
          </p>
        </div>

        <button
          type="button"
          disabled={busy || elapsedSec < 60}
          className="flex h-14 w-full items-center justify-center rounded-2xl bg-kilo-accent text-base font-bold text-black disabled:opacity-40"
          onClick={onComplete}
        >
          {busy ? t('app.profile.saving') : t('workout.calibration.cardio.finish')}
        </button>
        {elapsedSec < 60 ? (
          <p className="text-center text-xs text-kilo-muted">{t('workout.calibration.cardio.minDuration')}</p>
        ) : null}
        {error ? <p className="text-sm text-red-400">{error}</p> : null}
      </div>
    )
  }

  return (
    <div className={`fixed inset-0 z-[70] flex min-h-[60vh] flex-col gap-4 ${KILO.bg.page}`}>
      <header>
        <p className={KILO.meta}>Bio-Calibration</p>
        <h1 className={`mt-1 text-2xl ${KILO.heading}`}>{title}</h1>
        <p className={`mt-2 text-sm ${KILO.text.muted}`}>{subtitle}</p>
      </header>
      <BentoCard gradient="copper">
        <ul className={`space-y-2 text-sm ${KILO.text.muted}`}>
          <li>{t('workout.calibration.cardio.prepLine1')}</li>
          <li>{t('workout.calibration.cardio.prepLine2')}</li>
          <li>{t('workout.calibration.cardio.prepLine3')}</li>
        </ul>
        <button
          type="button"
          disabled={busy}
          className="mt-6 flex h-12 w-full items-center justify-center rounded-xl bg-kilo-accent text-sm font-bold text-black disabled:opacity-50"
          onClick={beginCountdown}
        >
          {t('workout.calibration.cardio.beginCountdown')}
        </button>
        {error ? <p className="mt-3 text-sm text-red-400">{error}</p> : null}
      </BentoCard>
    </div>
  )
}

export default CalibrationCardioTracker
